import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { STATIC_LANGUAGE_PAGES } from './staticLanguagePages';
import { fetchCurriculumData } from '../../services/dataApi';
import LanguageFlag from '../../components/LanguageFlag';

function useNoteSections(t) {
  return [
    {
      key: 'summary',
      icon: '📝',
      title: t('staticLanguagePage.notes.summaryTitle'),
      text: t('staticLanguagePage.notes.summaryText'),
    },
    {
      key: 'keyPoints',
      icon: '💡',
      title: t('staticLanguagePage.notes.keyPointsTitle'),
      text: t('staticLanguagePage.notes.keyPointsText'),
    },
    {
      key: 'quickReference',
      icon: '🔖',
      title: t('staticLanguagePage.notes.quickReferenceTitle'),
      text: t('staticLanguagePage.notes.quickReferenceText'),
    },
  ];
}

export default function StaticLanguagePage({ language }) {
  const { t } = useTranslation();
  const NOTE_SECTIONS = useNoteSections(t);
  const [quickSheet, setQuickSheet] = useState(null);
  const [sheetError, setSheetError] = useState(false);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${language.name} Summary Notes | SublingoX`;
    return () => {
      document.title = previousTitle;
    };
  }, [language.name]);

  useEffect(() => {
    let active = true;
    setQuickSheet(null);
    setSheetError(false);
    fetchCurriculumData(language.code, `quick-sheets/${language.code}.json`)
      .then((data) => {
        if (active) setQuickSheet(data);
      })
      .catch(() => {
        if (active) setSheetError(true);
      });
    return () => {
      active = false;
    };
  }, [language.code]);

  return (
    <div className="min-h-screen bg-slate-50">
      <section className={`bg-gradient-to-br ${language.accent} px-4 py-16 text-white sm:px-6 lg:px-8`}>
        <div className="mx-auto max-w-5xl">
          <Link
            to="/"
            className="mb-8 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-bold backdrop-blur-sm transition hover:bg-white/25"
          >
            <span aria-hidden="true">←</span>
            {t('staticLanguagePage.backToHome')}
          </Link>

          <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
            <LanguageFlag flag={language.flag} label={language.name} className="h-12 w-16 sm:h-[54px] sm:w-[72px]" />
            <div>
              <p className="text-xs font-black uppercase tracking-[0.25em] text-white/75">{t('staticLanguagePage.eyebrow')}</p>
              <h1 className="mt-2 text-4xl font-black sm:text-5xl">{t('staticLanguagePage.summaryTitleFallback', { lang: language.name })}</h1>
              <p className="mt-3 max-w-2xl text-lg text-white/85">
                {language.nativeName} · {language.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-5 md:grid-cols-3">
          {NOTE_SECTIONS.map((section) => (
            <section key={section.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <span className="text-3xl" aria-hidden="true">{section.icon}</span>
              <h2 className="mt-4 text-lg font-black text-slate-900">{section.title}</h2>
              {language.notes[section.key].length > 0 ? (
                <ul className="mt-3 space-y-2 text-sm leading-relaxed text-slate-600">
                  {language.notes[section.key].map((note) => <li key={note}>• {note}</li>)}
                </ul>
              ) : (
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{section.text}</p>
              )}
            </section>
          ))}
        </div>

        <section className="mt-10 scroll-mt-24" aria-labelledby="quick-sheet-title">
          <div className="overflow-hidden rounded-3xl border border-indigo-100 bg-white shadow-xl shadow-indigo-950/5">
            <div className="bg-gradient-to-r from-indigo-700 to-violet-700 px-5 py-6 text-white sm:px-8">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.2em] text-indigo-200">{t('staticLanguagePage.lastMinutePrep')}</p>
                  <h2 id="quick-sheet-title" className="mt-1 text-2xl font-black sm:text-3xl">
                    {quickSheet?.title || t('staticLanguagePage.quickSheetTitleFallback', { lang: language.name })}
                  </h2>
                  <p className="mt-2 max-w-2xl text-sm text-indigo-100">
                    {quickSheet?.subtitle || t('staticLanguagePage.loadingQuickSheet')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="print:hidden inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-black text-indigo-700 shadow-sm transition hover:bg-indigo-50"
                >
                  <span aria-hidden="true">⎙</span>
                  {t('staticLanguagePage.printQuickSheet')}
                </button>
              </div>
            </div>

            {!quickSheet && !sheetError && (
              <div className="grid gap-4 p-5 sm:grid-cols-2 sm:p-8" aria-label="Loading quick sheet">
                {[1, 2, 3, 4].map((item) => <div key={item} className="h-36 animate-pulse rounded-2xl bg-slate-100" />)}
              </div>
            )}

            {sheetError && (
              <p className="p-8 text-center text-sm font-semibold text-slate-600">
                {t('staticLanguagePage.quickSheetLoadError')}
              </p>
            )}

            {quickSheet && (
              <div className="p-4 sm:p-8">
                <nav className="print:hidden mb-6 flex gap-2 overflow-x-auto pb-2" aria-label="Quick-sheet sections">
                  {quickSheet.sections.map((section) => (
                    <a key={section.id} href={`#quick-${section.id}`} className="shrink-0 rounded-full border border-indigo-200 bg-indigo-50 px-4 py-2 text-xs font-black text-indigo-700 hover:bg-indigo-100">
                      {section.title}
                    </a>
                  ))}
                </nav>

                <aside className="mb-7 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                  <h3 className="text-sm font-black text-amber-900">{t('staticLanguagePage.threeExamReminders')}</h3>
                  <ul className="mt-2 grid gap-2 text-sm text-amber-950 md:grid-cols-3">
                    {quickSheet.examTips.map((tip) => <li key={tip} className="flex gap-2"><span aria-hidden="true">✓</span><span>{tip}</span></li>)}
                  </ul>
                </aside>

                <div className="space-y-8">
                  {quickSheet.sections.map((section) => (
                    <article id={`quick-${section.id}`} key={section.id} className="scroll-mt-24">
                      <h3 className="text-xl font-black text-slate-900">{section.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">{section.note}</p>
                      <div className="mt-3 overflow-x-auto rounded-2xl border border-slate-200">
                        <table className="w-full min-w-[640px] border-collapse text-left text-sm">
                          <thead className="bg-slate-100 text-slate-700">
                            <tr>{section.columns.map((column) => <th key={column} scope="col" className="border-b border-slate-200 px-4 py-3 font-black">{column}</th>)}</tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {section.rows.map((row, rowIndex) => (
                              <tr key={`${section.id}-${rowIndex}`} className="odd:bg-white even:bg-slate-50/60">
                                {row.map((cell, cellIndex) => cellIndex === 0
                                  ? <th key={cellIndex} scope="row" className="whitespace-nowrap px-4 py-3 font-bold text-slate-800">{cell}</th>
                                  : <td key={cellIndex} className="px-4 py-3 text-slate-700">{cell}</td>)}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="mt-10 rounded-3xl border border-indigo-100 bg-indigo-50 p-6 sm:p-8">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-widest text-indigo-600">{t('staticLanguagePage.continueLearning')}</p>
              <h2 className="mt-1 text-2xl font-black text-slate-900">{t('staticLanguagePage.exploreLessons', { lang: language.name })}</h2>
            </div>
            <Link
              to={`/learn/${language.code}`}
              className="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-700"
            >
              {t('staticLanguagePage.startLearning', { lang: language.name })}
            </Link>
          </div>
        </section>

        <nav className="mt-12" aria-label="Other static language pages">
          <p className="mb-4 text-xs font-black uppercase tracking-widest text-slate-500">{t('staticLanguagePage.otherLanguagePages')}</p>
          <div className="flex flex-wrap gap-3">
            {STATIC_LANGUAGE_PAGES.map((item) => (
              <Link
                key={item.slug}
                to={`/static/${item.slug}`}
                aria-current={item.slug === language.slug ? 'page' : undefined}
                className={`rounded-xl border px-4 py-2 text-sm font-bold transition ${
                  item.slug === language.slug
                    ? 'border-indigo-600 bg-indigo-600 text-white'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:text-indigo-700'
                }`}
              >
                {item.flag} {item.name}
              </Link>
            ))}
          </div>
        </nav>
      </main>
    </div>
  );
}
